import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Search, Plus, Edit, Trash2, GraduationCap, Loader2, Upload, Download, FileSpreadsheet, CheckCircle, AlertCircle, MoreHorizontal, ArrowUpDown, ArrowUp, ArrowDown, Video } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../../components/ui/dropdown-menu';
import { classOptionLabel, sortClassesByEnglishName } from '../../lib/classOptions';
import { BookSelect, useBooks } from '../../components/BooksFees';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Render an ISO YYYY-MM-DD (or any Date.parse-able string) as dd/mm/yyyy.
const formatDOB = (s) => {
    if (!s) return '—';
    const m = String(s).match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (m) {
        const [, y, mo, d] = m;
        return `${d.padStart(2, '0')}/${mo.padStart(2, '0')}/${y}`;
    }
    const t = Date.parse(s);
    if (isNaN(t)) return s;
    const dt = new Date(t);
    return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
};

const Students = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [students, setStudents] = useState([]);
    const [parents, setParents] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [classes, setClasses] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [special, setSpecial] = useState({ enabled: false, teacher_id: '', book: '', book_id: '' });
    const storageBooks = useBooks();
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortField, setSortField] = useState('name');
    const [sortDir, setSortDir] = useState('asc');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingStudent, setEditingStudent] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    // 'create' → add-new mode (existing endpoint); 'edit' → bulk-edit mode
    const [uploadMode, setUploadMode] = useState('create');
    const fileInputRef = useRef(null);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        arabic_name: '',
        date_of_birth: '',
        gender: '',
        address: '',
        parent_id: '',
        program_id: '',
        class_id: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [studentsRes, parentsRes, programsRes, classesRes, teachersRes] = await Promise.all([
                axios.get(`${API_URL}/api/students?status=active`, { withCredentials: true }),
                // include_all=true: lookup must include guardians who don't have active students yet
                axios.get(`${API_URL}/api/parents?include_all=true`, { withCredentials: true }),
                axios.get(`${API_URL}/api/programs`, { withCredentials: true }),
                axios.get(`${API_URL}/api/classes`, { withCredentials: true }),
                axios.get(`${API_URL}/api/teachers`, { withCredentials: true }).catch(() => ({ data: [] }))
            ]);
            setStudents(studentsRes.data);
            setParents(parentsRes.data);
            setPrograms(programsRes.data);
            setClasses(classesRes.data);
            setTeachers(teachersRes.data || []);
        } catch (error) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.gender || !formData.parent_id || !formData.program_id || !formData.class_id) {
            if (formData.parent_code && !formData.parent_id) {
                toast.error('No guardian found with that code — please double-check.');
            } else {
                toast.error('Please fill all required fields');
            }
            return;
        }
        setSaving(true);
        try {
            if (editingStudent) {
                await axios.put(`${API_URL}/api/students/${editingStudent.id}`, formData, { withCredentials: true });
                if (['super_admin', 'manager', 'branch_manager'].includes(user?.role)) {
                    const had = !!editingStudent.special_arabic;
                    if (special.enabled) {
                        if (!special.teacher_id) { toast.error('Choose the Special Arabic teacher'); setSaving(false); return; }
                        await axios.put(`${API_URL}/api/special-arabic/students/${editingStudent.id}`, { teacher_id: special.teacher_id, book: special.book, book_id: special.book_id || null }, { withCredentials: true });
                    } else if (had) {
                        await axios.delete(`${API_URL}/api/special-arabic/students/${editingStudent.id}`, { withCredentials: true });
                    }
                }
                toast.success('Student updated successfully');
            } else {
                await axios.post(`${API_URL}/api/students`, formData, { withCredentials: true });
                toast.success('Student added successfully');
            }
            setDialogOpen(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save student');
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (student) => {
        setEditingStudent(student);
        const parent = parents.find(p => p.id === student.parent_id);
        setFormData({
            first_name: student.first_name,
            last_name: student.last_name,
            arabic_name: student.arabic_name || '',
            date_of_birth: student.date_of_birth || '',
            gender: student.gender || '',
            address: student.address || '',
            parent_id: student.parent_id || '',
            parent_code: parent?.reg_code || '',
            program_id: student.program_id || '',
            class_id: student.class_id || '',
            reg_code: student.reg_code || ''
        });
        const sa = student.special_arabic || null;
        setSpecial({ enabled: !!sa, teacher_id: sa?.teacher_id || '', book: sa?.book || '', book_id: sa?.book_id || '' });
        setDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this student?')) return;
        try {
            await axios.delete(`${API_URL}/api/students/${id}`, { withCredentials: true });
            toast.success('Student deleted');
            fetchData();
        } catch (error) {
            toast.error('Failed to delete student');
        }
    };

    const isZoomStudent = (s) => (programs.find(p => p.id === s.program_id)?.location || '').toUpperCase() === 'ZOOM';
    const sendZoomEmail = async (s) => {
        try {
            const r = await axios.post(`${API_URL}/api/zoom/send`, { student_ids: [s.id] }, { withCredentials: true });
            if (r.data.classes_without_link?.length) toast.error(`No Zoom link set for class ${r.data.classes_without_link.join(', ')}`);
            else toast.success(`Zoom details emailed to ${r.data.emails_sent} guardian email(s)`);
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send'); }
    };

    const handleStatusChange = async (id, newStatus) => {
        try {
            await axios.put(`${API_URL}/api/students/${id}/status`, { status: newStatus }, { withCredentials: true });
            toast.success(`Student moved to ${newStatus.replace('_', ' ')}`);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update status');
        }
    };

    const resetForm = () => {
        setEditingStudent(null);
        setFormData({
            first_name: '', last_name: '', arabic_name: '', date_of_birth: '',
            gender: '', address: '', parent_id: '', parent_code: '', program_id: '', class_id: ''
        });
    };

    const handleDownloadTemplate = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/students/upload/template`, {
                withCredentials: true,
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'students_template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Template downloaded');
        } catch (error) {
            toast.error('Failed to download template');
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
            toast.error('Please upload an Excel file (.xlsx or .xls)');
            return;
        }
        setUploading(true);
        setUploadResult(null);
        const fd = new FormData();
        fd.append('file', file);
        const endpoint = uploadMode === 'edit'
            ? '/api/students/upload/edit'
            : uploadMode === 'last_year'
                ? '/api/students/upload/last-year'
                : '/api/students/upload';
        try {
            const response = await axios.post(`${API_URL}${endpoint}`, fd, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setUploadResult(response.data);
            const okCount = response.data.updated ?? response.data.created ?? 0;
            const label = uploadMode === 'edit' ? 'updated' : uploadMode === 'last_year' ? 'records saved' : 'imported';
            if (okCount > 0) {
                toast.success(`${okCount} student(s) ${label} successfully`);
                if (uploadMode !== 'last_year') fetchData();
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Upload failed');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDownloadEditTemplate = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/students/upload/edit/template`, {
                withCredentials: true,
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'students_edit_template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toast.error('Failed to download edit template');
        }
    };

    const handleDownloadLastYearTemplate = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/students/upload/last-year/template`, {
                withCredentials: true,
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'student_last_year_template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toast.error('Failed to download last-year template');
        }
    };

    const getParentName = (id) => {
        const p = parents.find(p => p.id === id);
        return p ? `${p.first_name} ${p.last_name}` : 'N/A';
    };
    const getClassArabicName = (id) => { const c = classes.find(c => c.id === id); return c?.arabic_name || c?.name || 'N/A'; };
    const getClassCode = (id) => classes.find(c => c.id === id)?.code || '—';
    const getProgramName = (id) => programs.find(p => p.id === id)?.name || 'N/A';
    const getTeacherName = (classId) => {
        if (!classId) return null;
        const c = classes.find(cl => cl.id === classId);
        if (!c || !c.main_teacher_id) return null;
        const t = teachers.find(t => t.id === c.main_teacher_id);
        if (!t) return null;
        return `${t.first_name || ''} ${t.last_name || ''}`.trim() || null;
    };

    const toggleSort = (field) => {
        if (sortField === field) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDir('asc');
        }
    };

    const SortIcon = ({ field }) => {
        if (sortField !== field) return <ArrowUpDown className="h-3 w-3 inline ml-1 opacity-40" />;
        return sortDir === 'asc' ? <ArrowUp className="h-3 w-3 inline ml-1" /> : <ArrowDown className="h-3 w-3 inline ml-1" />;
    };

    const filteredClasses = formData.program_id
        ? classes.filter(c => c.program_id === formData.program_id)
        : classes;

    const filteredStudents = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        const qDigits = q.replace(/[^0-9+]/g, '');
        const normalizeMobile = (m) => (m || '').replace(/[^0-9+]/g, '');
        const parentById = new Map(parents.map(p => [p.id, p]));
        const base = students.filter(s => {
            if (!q) return true;
            if ((s.first_name || '').toLowerCase().includes(q)) return true;
            if ((s.last_name || '').toLowerCase().includes(q)) return true;
            if ((s.reg_code || '').toLowerCase().includes(q)) return true;
            if ((s.arabic_name || '').includes(searchQuery.trim())) return true;
            if (qDigits && (s.date_of_birth || '').replace(/[^0-9]/g, '').includes(qDigits.replace(/[^0-9]/g, ''))) return true;
            if (qDigits && (s.date_of_birth || '').split('-').reverse().join('').includes(qDigits.replace(/[^0-9]/g, ''))) return true;
            const p = parentById.get(s.parent_id);
            if (p && (`${p.first_name || ''} ${p.last_name || ''}`.toLowerCase().includes(q) || (p.reg_code || '').toLowerCase().includes(q))) return true;
            if (p && qDigits) {
                if (normalizeMobile(p.mobile).includes(qDigits)) return true;
                if (normalizeMobile(p.guardian2_mobile).includes(qDigits)) return true;
            }
            return false;
        });
        const cmp = (a, b) => {
            let av, bv;
            if (sortField === 'name') {
                av = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
                bv = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
            } else if (sortField === 'code') {
                av = (a.reg_code || '').toString();
                bv = (b.reg_code || '').toString();
            } else if (sortField === 'dob') {
                // Compare as real dates so unpadded "2010-9-5" sorts before "2010-10-1"
                const parse = (s) => {
                    if (!s) return null;
                    const t = Date.parse(s);
                    return isNaN(t) ? null : t;
                };
                av = parse(a.date_of_birth);
                bv = parse(b.date_of_birth);
                // Push missing DOBs to the end of any sort direction
                if (av == null && bv == null) return 0;
                if (av == null) return 1;
                if (bv == null) return -1;
            } else if (sortField === 'program') {
                av = (getProgramName(a.program_id) || '').toLowerCase();
                bv = (getProgramName(b.program_id) || '').toLowerCase();
            } else if (sortField === 'class') {
                av = (getClassArabicName(a.class_id) || '').toLowerCase();
                bv = (getClassArabicName(b.class_id) || '').toLowerCase();
            } else {
                av = ''; bv = '';
            }
            if (av < bv) return sortDir === 'asc' ? -1 : 1;
            if (av > bv) return sortDir === 'asc' ? 1 : -1;
            return 0;
        };
        return [...base].sort(cmp);
    }, [students, searchQuery, sortField, sortDir, programs, classes, parents]);

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="students-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Students</h1>
                        <p className="text-[#4B5563]">Manage all enrolled students</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <Badge variant="secondary">
                            <GraduationCap className="h-4 w-4 mr-1" />
                            {students.length} students
                        </Badge>
                        <Button
                            variant="outline"
                            onClick={async () => {
                                try {
                                    const res = await axios.get(`${API_URL}/api/students/export/excel`, { withCredentials: true, responseType: 'blob' });
                                    const url = window.URL.createObjectURL(new Blob([res.data]));
                                    const link = document.createElement('a');
                                    link.href = url; link.setAttribute('download', 'students.xlsx');
                                    document.body.appendChild(link); link.click(); link.remove();
                                    window.URL.revokeObjectURL(url);
                                    toast.success('Students exported');
                                } catch { toast.error('Failed to export'); }
                            }}
                            data-testid="export-students-btn"
                        >
                            <Download className="h-4 w-4 mr-2" />Export Excel
                        </Button>
                        <Button variant="outline" onClick={() => { setUploadResult(null); setUploadMode('create'); setUploadDialogOpen(true); }} data-testid="upload-students-btn">
                            <Upload className="h-4 w-4 mr-2" /> Upload Excel
                        </Button>
                        <Button variant="outline" onClick={() => { setUploadResult(null); setUploadMode('edit'); setUploadDialogOpen(true); }} data-testid="bulk-edit-students-btn">
                            <Edit className="h-4 w-4 mr-2" /> Bulk Edit Excel
                        </Button>
                        <Button variant="outline" onClick={() => { setUploadResult(null); setUploadMode('last_year'); setUploadDialogOpen(true); }} data-testid="upload-last-year-btn">
                            <FileSpreadsheet className="h-4 w-4 mr-2" /> Upload Books &amp; Marks
                        </Button>
                        <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-[#064E3B] hover:bg-[#047857]" data-testid="add-student-btn">
                            <Plus className="h-4 w-4 mr-2" /> Add Student
                        </Button>
                        {user?.role === 'super_admin' && (
                            <Button variant="outline" className="text-[#EF4444] border-[#EF4444]" onClick={async () => { if (!window.confirm('DELETE ALL students? This cannot be undone!')) return; if (!window.confirm('Are you absolutely sure?')) return; try { await axios.delete(`${API_URL}/api/students`, { withCredentials: true }); toast.success('All students deleted'); fetchData(); } catch { toast.error('Failed'); } }} data-testid="delete-all-students-btn"><Trash2 className="h-4 w-4 mr-2" />Delete All</Button>
                        )}
                    </div>
                </div>

                <Card className="card-surface">
                    <CardContent className="p-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
                            <Input placeholder="Search by name (English/Arabic), code, date of birth, guardian or mobile..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" data-testid="students-search" />
                        </div>
                    </CardContent>
                </Card>

                <Card className="card-surface">
                    <CardContent className="p-0">
                        {filteredStudents.length > 0 ? (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('name')} data-testid="sort-name">Student<SortIcon field="name" /></TableHead>
                                            <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('code')} data-testid="sort-code">Reg Code<SortIcon field="code" /></TableHead>
                                            <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('dob')} data-testid="sort-dob">DOB<SortIcon field="dob" /></TableHead>
                                            <TableHead>Parent</TableHead>
                                            <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('program')} data-testid="sort-program">Program<SortIcon field="program" /></TableHead>
                                            <TableHead>Class Code</TableHead>
                                            <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('class')} data-testid="sort-class">Class<SortIcon field="class" /></TableHead>
                                            <TableHead>Teacher</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filteredStudents.map((student) => (
                                            <TableRow key={student.id} data-testid={`student-row-${student.id}`}>
                                                <TableCell>
                                                    <div className="flex items-center space-x-3">
                                                        <div className="h-10 w-10 rounded-full bg-[#064E3B]/10 flex items-center justify-center">
                                                            <GraduationCap className="h-5 w-5 text-[#064E3B]" />
                                                        </div>
                                                        <div className="cursor-pointer hover:text-[#064E3B]" onClick={() => navigate(`/admin/students/${student.id}`)} data-testid={`open-student-${student.id}`}>
                                                            <div className="font-medium flex items-center flex-wrap gap-1">{student.first_name} {student.last_name}{student.special_arabic && <Badge className="bg-[#D97706] text-white text-[10px]" data-testid={`special-arabic-badge-${student.id}`}>Special Arabic</Badge>}</div>
                                                            <p className="text-xs text-[#4B5563]">{student.arabic_name}</p>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="tabular-nums">{student.reg_code}</TableCell>
                                                <TableCell className="tabular-nums text-sm">{formatDOB(student.date_of_birth)}</TableCell>
                                                <TableCell>{getParentName(student.parent_id)}</TableCell>
                                                <TableCell><Badge variant="outline">{getProgramName(student.program_id)}</Badge></TableCell>
                                                <TableCell><Badge variant="outline" className="font-mono tabular-nums">{getClassCode(student.class_id)}</Badge></TableCell>
                                                <TableCell>{getClassArabicName(student.class_id)}</TableCell>
                                                <TableCell className="text-sm">
                                                    {getTeacherName(student.class_id)
                                                        ? <span>{getTeacherName(student.class_id)}</span>
                                                        : <span className="italic text-[#9CA3AF]">Unassigned</span>}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button size="sm" variant="ghost" data-testid={`student-actions-${student.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            <DropdownMenuItem onClick={() => handleEdit(student)}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>
                                                            {isZoomStudent(student) && <DropdownMenuItem onClick={() => sendZoomEmail(student)} className="text-[#1E40AF]" data-testid={`send-zoom-${student.id}`}><Video className="h-4 w-4 mr-2" />Email Zoom details</DropdownMenuItem>}
                                                            <DropdownMenuSeparator />
                                                            <DropdownMenuItem onClick={() => handleStatusChange(student.id, 'waiting_list')} className="text-[#F59E0B]">Move to Waiting List</DropdownMenuItem>
                                                            <DropdownMenuItem onClick={() => handleStatusChange(student.id, 'unregistered')} className="text-[#6B7280]">Move to Unregistered</DropdownMenuItem>
                                                            <DropdownMenuSeparator />
                                                            <DropdownMenuItem onClick={() => handleDelete(student.id)} className="text-[#EF4444]"><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-12">
                                <GraduationCap className="h-12 w-12 text-[#4B5563]/30 mb-4" />
                                <p className="text-[#4B5563] mb-4">No students found</p>
                                <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-[#064E3B] hover:bg-[#047857]">
                                    <Plus className="h-4 w-4 mr-2" /> Add First Student
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-['Outfit']">{editingStudent ? 'Edit Student' : 'Add New Student'}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label>Student Code (auto-generated if empty)</Label>
                                <Input value={formData.reg_code || ''} onChange={(e) => setFormData({...formData, reg_code: e.target.value})} placeholder="e.g. 10002-01" className="font-mono" data-testid="student-reg-code" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>First Name</Label>
                                    <Input value={formData.first_name} onChange={(e) => setFormData({...formData, first_name: e.target.value})} required data-testid="student-first-name" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Last Name</Label>
                                    <Input value={formData.last_name} onChange={(e) => setFormData({...formData, last_name: e.target.value})} required data-testid="student-last-name" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Arabic Name</Label>
                                <Input value={formData.arabic_name} onChange={(e) => setFormData({...formData, arabic_name: e.target.value})} required data-testid="student-arabic-name" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Date of Birth</Label>
                                    <Input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})} required data-testid="student-dob" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Gender</Label>
                                    <Select value={formData.gender} onValueChange={(v) => setFormData({...formData, gender: v})}>
                                        <SelectTrigger data-testid="student-gender"><SelectValue placeholder="Select gender" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="male">Male</SelectItem>
                                            <SelectItem value="female">Female</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Address <span className="text-xs text-[#6B7280] font-normal">— optional</span></Label>
                                <StudentAddressField
                                    parent={parents.find(p => p.id === formData.parent_id) || null}
                                    value={formData.address}
                                    onChange={(v) => setFormData({...formData, address: v})}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Guardian Code {editingStudent && user?.role !== 'super_admin' && <span className="text-xs text-[#6B7280] font-normal">— only the super admin can change it</span>}</Label>
                                <Input
                                    value={formData.parent_code || ''}
                                    readOnly={editingStudent && user?.role !== 'super_admin'}
                                    onChange={(e) => {
                                        const code = e.target.value.trim();
                                        const matched = parents.find(p => (p.reg_code || '') === code);
                                        setFormData(prev => {
                                            const next = {
                                                ...prev,
                                                parent_code: e.target.value,
                                                parent_id: matched ? matched.id : '',
                                            };
                                            // Auto-fill student address from Guardian 1 if currently empty
                                            if (matched && !prev.address && matched.address) {
                                                next.address = matched.address;
                                            }
                                            return next;
                                        });
                                    }}
                                    placeholder="Type the guardian's reg code (e.g. 23001)"
                                    className={`font-mono ${editingStudent && user?.role !== 'super_admin' ? 'bg-[#F3F4F6]' : ''}`}
                                    data-testid="student-parent-code"
                                    required
                                />
                                {formData.parent_code && (
                                    formData.parent_id ? (
                                        <p className="text-xs text-[#10B981]">
                                            ✓ {parents.find(p => p.id === formData.parent_id)?.first_name} {parents.find(p => p.id === formData.parent_id)?.last_name}
                                        </p>
                                    ) : (
                                        <p className="text-xs text-[#EF4444]">No guardian found with this code</p>
                                    )
                                )}
                            </div>
                            <div className="space-y-2">
                                <Label>Program</Label>
                                <Select value={formData.program_id} onValueChange={(v) => setFormData({...formData, program_id: v, class_id: ''})}>
                                    <SelectTrigger data-testid="student-program"><SelectValue placeholder="Select program" /></SelectTrigger>
                                    <SelectContent>
                                        {programs.map(p => (
                                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Class</Label>
                                <Select value={formData.class_id} onValueChange={(v) => setFormData({...formData, class_id: v})}>
                                    <SelectTrigger data-testid="student-class"><SelectValue placeholder="Select class" /></SelectTrigger>
                                    <SelectContent>
                                        {sortClassesByEnglishName(filteredClasses).map(c => (
                                            <SelectItem key={c.id} value={c.id}>{classOptionLabel(c)}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            {editingStudent && ['super_admin', 'manager', 'branch_manager'].includes(user?.role) && (
                                <div className={`space-y-2 rounded-lg border p-3 ${special.enabled ? 'border-[#FCD34D] bg-[#FFFBEB]' : 'border-[#E5E7EB]'}`} data-testid="special-arabic-box">
                                    <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                                        <input type="checkbox" checked={special.enabled} onChange={(e) => setSpecial({ ...special, enabled: e.target.checked })} data-testid="special-arabic-toggle" />
                                        Special Arabic Class
                                    </label>
                                    {special.enabled && (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            <div className="space-y-1"><Label className="text-xs">Special Arabic teacher</Label>
                                                <Select value={special.teacher_id || 'none'} onValueChange={(v) => setSpecial({ ...special, teacher_id: v === 'none' ? '' : v })}>
                                                    <SelectTrigger data-testid="special-arabic-teacher"><SelectValue placeholder="Select teacher" /></SelectTrigger>
                                                    <SelectContent><SelectItem value="none">Select teacher</SelectItem>{teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}</SelectContent>
                                                </Select></div>
                                            <div className="space-y-1"><Label className="text-xs">Arabic book (from storage)</Label><BookSelect value={special.book_id} books={storageBooks} testId="special-arabic-book" onChange={(b) => setSpecial({ ...special, book_id: b ? b.id : '', book: b ? b.title : '' })} /></div>
                                        </div>
                                    )}
                                </div>
                            )}
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                                <Button type="submit" disabled={saving} className="bg-[#064E3B] hover:bg-[#047857]" data-testid="save-student-btn">
                                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                    {editingStudent ? 'Update' : 'Add Student'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle className="font-['Outfit'] flex items-center gap-2">
                                <FileSpreadsheet className="h-5 w-5 text-[#064E3B]" /> {
                                    uploadMode === 'edit' ? 'Bulk Edit Students Excel'
                                    : uploadMode === 'last_year' ? 'Upload Books & Marks (Previous Years)'
                                    : 'Upload Students Excel'
                                }
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            {uploadMode === 'edit' ? (
                                <div className="bg-[#FEF3C7] border border-[#FCD34D] rounded-lg p-4 text-sm text-[#92400E]">
                                    <p className="font-medium mb-1">Bulk edit mode</p>
                                    <p>Each row is matched by <code>student_code</code>. Blank cells keep the existing value. Set <code>class_code</code> to <code>none</code> to clear a class assignment.</p>
                                    <p className="mt-1 text-xs">Editable columns: first_name, last_name, arabic_name, first_name_ar, last_name_ar, date_of_birth, gender, address, guardian_code, program_name, class_code, status, discount_amount, notes.</p>
                                </div>
                            ) : uploadMode === 'last_year' ? (
                                <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-lg p-4 text-sm text-[#1E40AF]">
                                    <p className="font-medium mb-1">Previous years academic records</p>
                                    <p>Each row is one student for one year. Matched by <code>student_code</code>. Any column can be empty.</p>
                                    <p className="mt-1 text-xs">Columns: year, student_code, program_name, class_name, teacher_name, quran_curriculum (Quran studied), quran_mark, arabic_book, arabic_mark, tajweed_book, tajweed_mark, islamic_education_book, islamic_education_mark, absence_days.</p>
                                </div>
                            ) : (
                                <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg p-4 text-sm text-[#166534]">
                                    <p className="font-medium mb-1">Required columns:</p>
                                    <p>first_name, last_name, arabic_name, date_of_birth, gender, address, guardian_code, program_name, class_code</p>
                                    <p className="mt-1 text-xs">Guardian must be registered first. Use guardian reg code and class code.</p>
                                </div>
                            )}

                            <Button variant="outline" className="w-full" onClick={
                                uploadMode === 'edit' ? handleDownloadEditTemplate
                                : uploadMode === 'last_year' ? handleDownloadLastYearTemplate
                                : handleDownloadTemplate
                            } data-testid={
                                uploadMode === 'edit' ? 'download-students-edit-template'
                                : uploadMode === 'last_year' ? 'download-last-year-template'
                                : 'download-students-template'
                            }>
                                <Download className="h-4 w-4 mr-2" /> {
                                    uploadMode === 'edit' ? 'Download Current Students (Editable)'
                                    : uploadMode === 'last_year' ? 'Download Books & Marks Template'
                                    : 'Download Template'
                                }
                            </Button>

                            <div className="border-2 border-dashed border-[#D1D5DB] rounded-lg p-6 text-center">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={handleFileUpload}
                                    className="hidden"
                                    id="students-file-upload"
                                    data-testid="students-file-input"
                                />
                                <label htmlFor="students-file-upload" className="cursor-pointer">
                                    {uploading ? (
                                        <div className="flex flex-col items-center">
                                            <Loader2 className="h-8 w-8 animate-spin text-[#064E3B] mb-2" />
                                            <p className="text-sm text-[#4B5563]">Uploading...</p>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center">
                                            <Upload className="h-8 w-8 text-[#4B5563] mb-2" />
                                            <p className="text-sm font-medium text-[#1F2937]">Click to upload Excel file</p>
                                            <p className="text-xs text-[#4B5563] mt-1">.xlsx or .xls files only</p>
                                        </div>
                                    )}
                                </label>
                            </div>

                            {uploadResult && (
                                <div className="space-y-2" data-testid="upload-result">
                                    <div className="flex items-center gap-2 text-sm">
                                        <CheckCircle className="h-4 w-4 text-[#10B981]" />
                                        <span>{uploadResult.updated ?? uploadResult.created ?? 0} student(s) {uploadResult.updated != null ? 'updated' : 'created'}</span>
                                    </div>
                                    {uploadResult.skipped > 0 && (
                                        <div className="flex items-center gap-2 text-sm text-[#F59E0B]">
                                            <AlertCircle className="h-4 w-4" />
                                            <span>{uploadResult.skipped} row(s) skipped</span>
                                        </div>
                                    )}
                                    {uploadResult.errors.length > 0 && (
                                        <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-3 max-h-32 overflow-y-auto">
                                            {uploadResult.errors.map((err, i) => (
                                                <p key={`err-${i}-${err.slice(0,30)}`} className="text-xs text-[#DC2626]">{err}</p>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Close</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
};

export default Students;

// Reusable address picker:
// - When parent has BOTH address (Guardian 1) AND a non-empty different guardian2_address →
//   show dropdown [Guardian 1 / Guardian 2 / Custom] + input
// - Otherwise → simple text input (still optional)
const StudentAddressField = ({ parent, value, onChange }) => {
    const g1 = (parent?.address || '').trim();
    const g2 = (parent?.guardian2_address || '').trim();
    const hasTwoDistinct = Boolean(g1 && g2 && g1.toLowerCase() !== g2.toLowerCase());

    const detectMode = (v) => {
        const t = (v || '').trim();
        if (!t) return g1 ? 'g1' : (g2 ? 'g2' : 'custom');
        if (t.toLowerCase() === g1.toLowerCase()) return 'g1';
        if (t.toLowerCase() === g2.toLowerCase()) return 'g2';
        return 'custom';
    };
    const [mode, setMode] = React.useState(detectMode(value));
    React.useEffect(() => { setMode(detectMode(value)); /* eslint-disable-next-line */ }, [value, g1, g2]);

    if (!hasTwoDistinct) {
        return (
            <Input
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder={g1 ? `Defaults to: ${g1}` : 'Optional'}
                data-testid="student-address"
            />
        );
    }
    return (
        <div className="space-y-2">
            <Select
                value={mode}
                onValueChange={(m) => {
                    setMode(m);
                    if (m === 'g1') onChange(g1);
                    else if (m === 'g2') onChange(g2);
                    else onChange('');
                }}
            >
                <SelectTrigger data-testid="student-address-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="g1">Guardian 1: {g1}</SelectItem>
                    <SelectItem value="g2">Guardian 2: {g2}</SelectItem>
                    <SelectItem value="custom">Custom (type below)</SelectItem>
                </SelectContent>
            </Select>
            {mode === 'custom' && (
                <Input
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="Enter custom address"
                    data-testid="student-address"
                />
            )}
        </div>
    );
};
